/**
 * Typed API client for the PRM Portal
 * Ported from frontend/src/lib/api.js with TypeScript types
 */

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public fieldErrors?: Record<string, string>
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Retry configuration for resilient API calls
const RETRY_CONFIG = {
  maxRetries: 2,
  retryDelay: 1000,      // 1 second between retries
  timeout: 5000,         // 5 seconds normal timeout
  finalTimeout: 10000,   // 10 seconds for final attempt
  retryableStatuses: [500, 502, 503, 504],
};

// Custom error to identify our timeout vs other aborts
const TIMEOUT_REASON = 'ApiClientTimeout';

/**
 * Fetch with automatic retry on network errors and 5xx responses.
 * Uses progressive timeouts: 5s for first attempts, 10s for final attempt.
 * Retries ONCE on first timeout (handles cold starts), but not subsequent timeouts.
 */
async function fetchWithRetry(
  url: string,
  config: RequestInit,
  retriesLeft = RETRY_CONFIG.maxRetries,
  hasRetriedTimeout = false
): Promise<Response> {
  // Use extended timeout on final attempt
  const timeoutMs = retriesLeft === 0 ? RETRY_CONFIG.finalTimeout : RETRY_CONFIG.timeout;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(TIMEOUT_REASON), timeoutMs);

  try {
    const response = await fetch(url, { ...config, signal: controller.signal });
    clearTimeout(timer);

    // Retry on 5xx errors (server errors that may be transient)
    if (RETRY_CONFIG.retryableStatuses.includes(response.status) && retriesLeft > 0) {
      await new Promise(r => setTimeout(r, RETRY_CONFIG.retryDelay));
      return fetchWithRetry(url, config, retriesLeft - 1, hasRetriedTimeout);
    }

    return response;
  } catch (error) {
    clearTimeout(timer);

    const isNetworkError = error instanceof TypeError;
    const isOurTimeout = error instanceof DOMException &&
      error.name === 'AbortError' &&
      controller.signal.reason === TIMEOUT_REASON;

    // Retry on network errors
    if (retriesLeft > 0 && isNetworkError) {
      await new Promise(r => setTimeout(r, RETRY_CONFIG.retryDelay));
      return fetchWithRetry(url, config, retriesLeft - 1, hasRetriedTimeout);
    }

    // Retry ONCE on timeout (handles cold starts), but not repeatedly
    if (isOurTimeout && !hasRetriedTimeout && retriesLeft > 0) {
      await new Promise(r => setTimeout(r, RETRY_CONFIG.retryDelay));
      return fetchWithRetry(url, config, retriesLeft - 1, true);
    }

    throw error;
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl = '/api') {
    this.baseUrl = baseUrl;
  }

  private async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { body, ...init } = options;

    const config: RequestInit = {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...init.headers,
      },
      credentials: 'include', // Include cookies for session auth
    };

    if (body !== undefined) {
      config.body = JSON.stringify(body);
    }

    const response = await fetchWithRetry(`${this.baseUrl}${endpoint}`, config);

    // Handle empty responses
    const contentType = response.headers.get('content-type');
    let data: T | null = null;

    if (contentType?.includes('application/json')) {
      data = await response.json();
    }

    if (!response.ok) {
      const errorData = data as { error?: string; fieldErrors?: Record<string, string> } | null;
      const errorMessage = errorData?.error || `Request failed with status ${response.status}`;
      const fieldErrors = errorData?.fieldErrors;
      throw new ApiError(errorMessage, response.status, undefined, fieldErrors);
    }

    return data as T;
  }

  async get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T>(endpoint: string, body: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'POST', body });
  }

  async put<T>(endpoint: string, body: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PUT', body });
  }

  async patch<T>(endpoint: string, body: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PATCH', body });
  }

  async delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  // File upload - uses FormData instead of JSON
  async upload<T>(endpoint: string, formData: FormData): Promise<T> {
    const response = await fetchWithRetry(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
      // Don't set Content-Type header - browser will set it with boundary
    });

    const contentType = response.headers.get('content-type');
    let data: T | null = null;

    if (contentType?.includes('application/json')) {
      data = await response.json();
    }

    if (!response.ok) {
      const errorData = data as { error?: string; fieldErrors?: Record<string, string> } | null;
      const errorMessage = errorData?.error || `Upload failed with status ${response.status}`;
      const fieldErrors = errorData?.fieldErrors;
      throw new ApiError(errorMessage, response.status, undefined, fieldErrors);
    }

    return data as T;
  }

  // File upload with PUT - for replacing existing files
  async uploadPut<T>(endpoint: string, formData: FormData): Promise<T> {
    const response = await fetchWithRetry(`${this.baseUrl}${endpoint}`, {
      method: 'PUT',
      body: formData,
      credentials: 'include',
    });

    const contentType = response.headers.get('content-type');
    let data: T | null = null;

    if (contentType?.includes('application/json')) {
      data = await response.json();
    }

    if (!response.ok) {
      const errorData = data as { error?: string; fieldErrors?: Record<string, string> } | null;
      const errorMessage = errorData?.error || `Upload failed with status ${response.status}`;
      const fieldErrors = errorData?.fieldErrors;
      throw new ApiError(errorMessage, response.status, undefined, fieldErrors);
    }

    return data as T;
  }
}

// Default API client instance
// Use VITE_API_URL for the API base, fallback to /api for same-origin
const apiBaseUrl = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api`
  : '/api';

export const api = new ApiClient(apiBaseUrl);

// Export class for testing or custom instances
export { ApiClient };
